import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Dropdown} from 'primereact/dropdown';
import {requestFatca} from './fatcaApi';

const IRS_SOURCE='https://www.irs.gov/businesses/international-businesses/united-states-income-tax-treaties-a-to-z';

export default function TreatyCountriesEditor() {
  const [saved,setSaved]=useState(null),[selected,setSelected]=useState([]);
  const [loading,setLoading]=useState(true),[saving,setSaving]=useState(false);
  const [error,setError]=useState(''),[success,setSuccess]=useState(''),[filter,setFilter]=useState('');
  const requestRef=useRef(null);
  const refresh=async()=>{
    requestRef.current?.abort();
    const controller=new AbortController();requestRef.current=controller;
    setLoading(true);setError('');setSuccess('');
    try {
      const data=await requestFatca('treaty-countries',{signal:controller.signal});
      setSaved(data);setSelected(data.isoCodes);
    } catch(e){if(e.name!=='AbortError')setError(e.message);}
    finally{if(!controller.signal.aborted)setLoading(false);}
  };
  useEffect(()=>{refresh();return()=>requestRef.current?.abort();},[]);
  const countries=useMemo(()=>[...(saved?.countries || [])].sort((a,b)=>a.name.localeCompare(b.name)),[saved]);
  const available=countries.filter(country=>!selected.includes(country.isoCode));
  const rows=countries.filter(country=>selected.includes(country.isoCode) && `${country.name} ${country.isoCode}`.toLowerCase().includes(filter.trim().toLowerCase()));
  const dirty=!!saved && JSON.stringify([...selected].sort())!==JSON.stringify([...saved.isoCodes].sort());
  const disabled=loading || saving;
  const change=next=>{setSelected(next);setSuccess('');};
  const save=async()=>{
    if(!dirty || disabled)return;
    const controller=new AbortController();requestRef.current=controller;
    setSaving(true);setError('');setSuccess('');
    try {
      const data=await requestFatca('treaty-countries',{body:{isoCodes:selected,revision:saved.revision},signal:controller.signal});
      setSaved(data);setSelected(data.isoCodes);setSuccess('Treaty country list saved.');
    } catch(e){if(e.name!=='AbortError')setError(e.message);}
    finally{if(!controller.signal.aborted)setSaving(false);}
  };
  return <section className="card shadow-sm mb-3" aria-labelledby="treaty-countries-title">
    <div className="p-3 border-bottom d-flex justify-content-between align-items-center gap-2 flex-wrap">
      <div>
        <div id="treaty-countries-title" className="fw-bold" style={{color:'#4C5465'}}>U.S. income tax treaty countries</div>
        <div className="small text-muted">Manage countries listed by the IRS. Inclusion does not confirm eligibility for treaty benefits.</div>
      </div>
      <div className="d-flex gap-2 flex-wrap">
        <a className="btn btn-outline-secondary btn-sm" href={IRS_SOURCE} target="_blank" rel="noreferrer">View IRS list <i className="bi bi-box-arrow-up-right ms-1" aria-hidden="true"/></a>
        <a className="btn btn-outline-secondary btn-sm" href="https://www.swissbanking.ch/en/topics/tax/double-tax-treaties-dtts" target="_blank" rel="noreferrer">View KPMG list <i className="bi bi-box-arrow-up-right ms-1" aria-hidden="true"/></a>
      </div>
    </div>
    <div className="p-3">
      {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
      {success && <div className="alert alert-success py-2" role="status">{success}</div>}
      {loading ? <div className="text-muted small" role="status">Loading countries…</div> : saved && <>
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <label className="form-label small" htmlFor="treaty-country-add">Add a country</label>
            <Dropdown inputId="treaty-country-add" value={null} options={available} optionLabel="name" optionValue="isoCode"
              filter filterBy="name,isoCode" resetFilterOnHide placeholder="Search and select a country" className="w-100"
              disabled={disabled} onChange={e=>{if(e.value)change([...selected,e.value]);}} emptyMessage="All countries have been added"/>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label small" htmlFor="treaty-country-filter">Filter the table</label>
            <input id="treaty-country-filter" className="form-control" type="search" placeholder="Country name or ISO code" value={filter} onChange={e=>setFilter(e.target.value)}/>
          </div>
        </div>
        <div className="table-responsive border rounded" style={{maxHeight:360,overflowY:'auto'}}>
          <table className="table table-sm align-middle mb-0">
            <thead style={{position:'sticky',top:0,zIndex:1,background:'#F2F4F7'}}><tr>
              <th scope="col" className="px-3 py-2">Country</th><th scope="col" className="py-2">ISO code</th><th scope="col" className="text-end px-3 py-2">Action</th>
            </tr></thead>
            <tbody>{rows.map(country=><tr key={country.isoCode}>
              <td className="px-3">{country.name}</td><td className="text-muted small">{country.isoCode}</td>
              <td className="text-end px-3"><button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled}
                aria-label={`Remove ${country.name} from treaty list`} onClick={()=>change(selected.filter(code=>code!==country.isoCode))}>Remove</button></td>
            </tr>)}{!rows.length && <tr><td colSpan="3" className="text-muted text-center p-3">{selected.length?'No countries match your search.':'No countries in the list.'}</td></tr>}</tbody>
          </table>
        </div>
      </>}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
        <div className="small text-muted">{saved && <><span>{selected.length} countries{dirty?' · Unsaved changes':''}</span><br/>Last updated: {saved.updatedAt?new Date(saved.updatedAt).toLocaleString():'—'}</>}</div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={disabled} onClick={refresh}>{dirty?'Discard changes and reload':'Reload saved list'}</button>
          <button type="button" className="btn btn-activation btn-sm" disabled={disabled || !dirty} onClick={save}>{saving?'Saving…':'Save changes'}</button>
        </div>
      </div>
    </div>
  </section>;
}
